import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/auth/useAuth'
import { ROUTES } from '../../constants/routes'
import { Link as LinkUI, Logo } from '.'
import PropTypes from 'prop-types'
import { ConversationHistory } from '../chat/ConversationHistory'

export const SlidingSidebar = ({ isOpen, onClose, conversationHistoryRef }) => {
  const { user, isAuthenticated, signOut } = useAuth()
  const navigate = useNavigate()
  const sidebarRef = useRef(null)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the sidebar
      if (sidebarRef.current && sidebarRef.current.contains(event.target)) {
        return
      }

      // Don't close if clicking inside a modal (portal)
      const modalRoot = document.getElementById('modal-root')
      if (modalRoot && modalRoot.contains(event.target)) {
        return
      }

      // Close the sidebar for all other clicks
      onClose()
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleSignOut = async () => {
    try {
      await signOut()
      onClose()
      // Force reload the page to reset all state
      window.location.href = ROUTES.HOME
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handleNewConversation = () => {
    // Clear any resumed conversation
    localStorage.removeItem('resumedConversation')
    localStorage.removeItem('resumedConversationId')
    localStorage.removeItem('conversationHistory')
    // Navigate to home to start fresh
    navigate(ROUTES.HOME)
    onClose()
    // Force reload to reset all state
    window.location.href = ROUTES.HOME
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 800))
    setLoginLoading(false)
    setLoginError('This email address is not associated with an account.')
  }

  const handleConversationSelect = (conversationId) => {
    // Navigate to the conversation resume route
    navigate(`/conversation/${conversationId}`)
    onClose()
  }

  return (
    <>
      {/* Backdrop - provides content for glassmorphism but doesn't blur itself */}
      <div
        className={`fixed inset-0 bg-black/40 transition-all duration-300 z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ height: '100dvh' }}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 w-80 glassmorphism-sidebar transform transition-transform duration-300 z-50 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ height: '100dvh' }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border">
            <Link
              to={ROUTES.HOME}
              onClick={onClose}
              className="hover:opacity-80 transition-opacity"
            >
              <Logo size="small" className="text-white" />
            </Link>
            <button
              onClick={onClose}
              className="p-2 hover:bg-dark-tertiary rounded-lg transition-all duration-200 text-gray-400 hover:text-white"
              aria-label="Close sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* User Section */}
            {isAuthenticated && user ? (
              <div className="p-6 border-b border-dark-border">
                <div className="mb-4">
                  <p className="text-sm text-gray-400">Signed in as</p>
                  <p className="text-white font-medium">{user.email}</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-2 bg-dark-tertiary hover:bg-gray-800 border border-dark-border hover:border-gray-600 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-all duration-200"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="p-6 border-b border-dark-border">
                <p className="text-sm font-medium text-gray-300 mb-4">Sign In</p>
                <form onSubmit={handleLogin} className="space-y-3">
                  <input
                    type="email"
                    placeholder="Email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-dark-tertiary border border-dark-border rounded-lg text-sm text-gray-300 placeholder-gray-600 focus:outline-none focus:border-gray-500"
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-dark-tertiary border border-dark-border rounded-lg text-sm text-gray-300 placeholder-gray-600 focus:outline-none focus:border-gray-500"
                  />
                  {loginError && <p className="text-xs text-red-400">{loginError}</p>}
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full px-4 py-2 bg-dark-tertiary hover:bg-gray-800 border border-dark-border hover:border-gray-600 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50"
                  >
                    {loginLoading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              </div>
            )}

            {/* Navigation Links */}
            <nav className="p-6">
              <ul className="space-y-1">
                <li>
                  <Link
                    to={ROUTES.HOME}
                    onClick={onClose}
                    className="flex items-center px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-tertiary rounded-lg transition-colors"
                  >
                    <svg
                      className="w-5 h-5 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.ABOUT}
                    onClick={onClose}
                    className="flex items-center px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-tertiary rounded-lg transition-colors"
                  >
                    <svg
                      className="w-5 h-5 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.HELP}
                    onClick={onClose}
                    className="flex items-center px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-tertiary rounded-lg transition-colors"
                  >
                    <svg
                      className="w-5 h-5 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Help
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.CONTACT}
                    onClick={onClose}
                    className="flex items-center px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-tertiary rounded-lg transition-colors"
                  >
                    <svg
                      className="w-5 h-5 mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Conversation History Section - Only for authenticated users when enabled */}
            {isAuthenticated && import.meta.env.VITE_CONVERSATION_HISTORY_ENABLED === 'true' && (
              <div className="border-t border-dark-border">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-400">Conversations</h3>
                    <button
                      onClick={handleNewConversation}
                      className="text-xs text-gray-500 hover:text-white transition-colors"
                    >
                      New Chat
                    </button>
                  </div>
                  <ConversationHistory
                    ref={conversationHistoryRef}
                    onSelectConversation={handleConversationSelect}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-dark-border">
            <div className="text-xs text-gray-500 space-y-3 text-center">
              <div className="flex justify-center space-x-4">
                <LinkUI to={ROUTES.PRIVACY_POLICY} onClick={onClose} variant="footer">
                  Privacy Policy
                </LinkUI>
                <LinkUI to={ROUTES.TERMS_OF_SERVICE} onClick={onClose} variant="footer">
                  Terms of Service
                </LinkUI>
              </div>
              <p>&copy; {new Date().getFullYear()} Et Ai Labs</p>

            </div>
          </div>
        </div>
      </div>
    </>
  )
}

SlidingSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conversationHistoryRef: PropTypes.object,
}
